import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useI18nLanguage } from '../hooks/useI18nLanguage';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const DEFAULT_CURRENCY = 'USD';
/** How often to refresh rates from the backend (which uses Frankfurter / ECB data). */
const RATES_REFRESH_MS = 5 * 60 * 1000;
const WORKSPACE_RATES_REFRESH_MS = 60 * 1000;

function intlCurrencyAmount(locale: string, currencyCode: string, amount: number): string {
  const loc = locale || 'en';
  const code = (currencyCode || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
}

export type SupportedCurrencyItem = {
  id: string;
  code: string;
  name: string | null;
  symbol: string | null;
  sortOrder: number;
};

type RatesResponse = { base: string; date: string; rates: Record<string, number> } & FxMeta;
type FxMeta = {
  rateSource?: 'provider' | 'cache' | 'manual' | 'fallback';
  isStale?: boolean;
  asOf?: string;
  markupPct?: number;
  roundingProfile?: 'PSYCH_99' | 'PSYCH_95' | 'ROUND_INT' | string;
  enabled?: boolean;
};

function applyFxRounding(amount: number, profile?: string): number {
  if (!Number.isFinite(amount)) return 0;
  if (profile === 'ROUND_INT') return Math.round(amount);
  if (profile === 'PSYCH_95') return Math.max(0, Math.floor(amount) + 0.95);
  if (profile === 'PSYCH_99') return Math.max(0, Math.floor(amount) + 0.99);
  return Number(amount.toFixed(2));
}

const PRICE_LOADING_TEXT = '...';

export type CurrencyContextValue = {
  currency: string;
  setCurrency: (code: string) => void;
  isCurrencySwitching: boolean;
  supportedCurrencies: SupportedCurrencyItem[];
  rates: Record<string, number>;
  ratesMeta: FxMeta;
  /** Amounts in catalog/checkout are stored in USD; converts to the selected currency using live rates. */
  formatPrice: (amountInBaseUsd: number) => string;
  /** Convert from any supported currency into the selected currency, then format for the current UI language. */
  formatPriceFrom: (amount: number, fromCurrency: string) => string;
  /** Amount is already in `currencyCode` (no FX); format with the current UI locale (e.g. backoffice rows). */
  formatMajorInCurrency: (amount: number, currencyCode: string) => string;
  refreshRates: () => Promise<void>;
  refreshSupported: () => Promise<void>;
  loading: boolean;
};

const defaultContextValue: CurrencyContextValue = {
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
  isCurrencySwitching: false,
  supportedCurrencies: [],
  rates: {},
  ratesMeta: {},
  formatPrice: (n) => (n == null || Number.isNaN(n) ? '' : Number(n).toFixed(2)),
  formatPriceFrom: (n) => (n == null || Number.isNaN(n) ? '' : Number(n).toFixed(2)),
  formatMajorInCurrency: (n, c) => (n == null || Number.isNaN(n) ? '' : `${Number(n).toFixed(2)} ${c}`),
  refreshRates: async () => {},
  refreshSupported: async () => {},
  loading: true,
};

export const CurrencyContext = React.createContext<CurrencyContextValue>(defaultContextValue);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, fetchPreferences, updatePreferences } = useAuth();
  const locale = useI18nLanguage();
  const { pathname } = useLocation();
  const isWorkspaceRoute = pathname.startsWith('/workspace');

  const [currency, setCurrencyState] = useState<string>(() =>
    typeof window !== 'undefined' ? window.localStorage.getItem('lewouhmart_currency') || DEFAULT_CURRENCY : DEFAULT_CURRENCY,
  );
  const [supportedCurrencies, setSupportedCurrencies] = useState<SupportedCurrencyItem[]>([]);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [ratesMeta, setRatesMeta] = useState<FxMeta>({});
  const [loading, setLoading] = useState(true);
  const [isCurrencySwitching, setIsCurrencySwitching] = useState(false);
  const [preferencesResolved, setPreferencesResolved] = useState(false);
  const switchSeqRef = useRef(0);
  const fxEnabledForNewUsers = ratesMeta.enabled !== false;
  const canUseFxForThisUser = fxEnabledForNewUsers;
  const fxDisplayReady = useMemo(() => {
    if (loading) return false;
    if (isCurrencySwitching) return false;
    if (!preferencesResolved) return false;
    if (currency === DEFAULT_CURRENCY) return true;
    const rate = Number(rates[currency] ?? 0);
    return Number.isFinite(rate) && rate > 0;
  }, [currency, isCurrencySwitching, loading, preferencesResolved, rates]);

  const loadSupported = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/currencies/supported`);
      if (!res.ok) return;
      const data = (await res.json()) as SupportedCurrencyItem[];
      setSupportedCurrencies(data);
    } catch {
      // keep previous
    }
  }, []);

  const refreshRates = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/currencies/rates`);
      if (!res.ok) return;
      const data = (await res.json()) as RatesResponse;
      setRates(data.rates ?? {});
      const meta = data as RatesResponse & FxMeta;
      setRatesMeta({
        rateSource: meta.rateSource,
        isStale: meta.isStale,
        asOf: meta.asOf,
        markupPct: meta.markupPct,
        roundingProfile: meta.roundingProfile,
        enabled: meta.enabled,
      });
    } catch {
      // keep previous rates
    }
  }, []);

  const refreshSupported = useCallback(async () => {
    await loadSupported();
  }, [loadSupported]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      await loadSupported();
      await refreshRates();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSupported, refreshRates]);

  useEffect(() => {
    const intervalMs = isWorkspaceRoute
      ? WORKSPACE_RATES_REFRESH_MS
      : RATES_REFRESH_MS;
    const t = setInterval(refreshRates, intervalMs);
    return () => clearInterval(t);
  }, [isWorkspaceRoute, refreshRates]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshRates();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshRates]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'lewouhmart_currency' || !e.newValue) return;
      setCurrencyState(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setPreferencesResolved(true);
      return;
    }
    setPreferencesResolved(false);
    let cancelled = false;
    fetchPreferences()
      .then((prefs) => {
        if (!prefs?.currency || cancelled) return;
        const pref = prefs.currency.toUpperCase();
        setCurrencyState(pref);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('lewouhmart_currency', pref);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPreferencesResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, fetchPreferences]);

  useEffect(() => {
    const mustForceUsd = !canUseFxForThisUser || isWorkspaceRoute;
    if (!mustForceUsd) return;
    if (currency === DEFAULT_CURRENCY) return;
    setCurrencyState(DEFAULT_CURRENCY);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('lewouhmart_currency', DEFAULT_CURRENCY);
    }
  }, [canUseFxForThisUser, currency, isWorkspaceRoute]);

  const setCurrency = useCallback(
    (code: string) => {
      const nextCode = code.toUpperCase();
      if (!canUseFxForThisUser && nextCode !== DEFAULT_CURRENCY) {
        return;
      }
      if (isWorkspaceRoute && nextCode !== DEFAULT_CURRENCY) {
        return;
      }
      if (nextCode === currency) return;
      const seq = ++switchSeqRef.current;
      const startedAt = Date.now();
      setIsCurrencySwitching(true);
      setCurrencyState(nextCode);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('lewouhmart_currency', nextCode);
      }
      if (isAuthenticated) {
        updatePreferences({ currency: nextCode }).catch(() => {});
      }
      void refreshRates().finally(() => {
        const elapsed = Date.now() - startedAt;
        const minMs = 500;
        const waitMs = elapsed < minMs ? minMs - elapsed : 0;
        window.setTimeout(() => {
          if (switchSeqRef.current === seq) setIsCurrencySwitching(false);
        }, waitMs);
      });
    },
    [canUseFxForThisUser, currency, isAuthenticated, isWorkspaceRoute, refreshRates, updatePreferences],
  );

  const formatMajorInCurrency = useCallback(
    (amount: number, currencyCode: string): string => {
      if (amount == null || Number.isNaN(amount)) return '';
      if (!fxDisplayReady) return PRICE_LOADING_TEXT;
      return intlCurrencyAmount(locale, currencyCode, amount);
    },
    [fxDisplayReady, locale],
  );

  const formatPrice = useCallback(
    (amountInBaseUsd: number): string => {
      if (amountInBaseUsd == null || Number.isNaN(amountInBaseUsd)) return '';
      if (!fxDisplayReady) return PRICE_LOADING_TEXT;
      if (isWorkspaceRoute || !canUseFxForThisUser || currency === DEFAULT_CURRENCY) {
        // Backoffice and FX-disabled mode always show base USD amounts.
        return intlCurrencyAmount(locale, DEFAULT_CURRENCY, Number(amountInBaseUsd));
      }
      const rate = currency === DEFAULT_CURRENCY ? 1 : (rates[currency] ?? 0);
      // Prevent leaking base USD amount as another currency before FX rates are loaded.
      if (!rate) return currency === DEFAULT_CURRENCY ? intlCurrencyAmount(locale, currency, amountInBaseUsd) : PRICE_LOADING_TEXT;
      const markupFactor =
        isWorkspaceRoute || currency === DEFAULT_CURRENCY
          ? 1
          : 1 + Number(ratesMeta.markupPct ?? 0) / 100;
      const rawValue = amountInBaseUsd * rate * markupFactor;
      const value = isWorkspaceRoute
        ? Number(rawValue.toFixed(2))
        : applyFxRounding(rawValue, ratesMeta.roundingProfile);
      return intlCurrencyAmount(locale, currency, value);
    },
    [canUseFxForThisUser, currency, fxDisplayReady, isWorkspaceRoute, rates, locale, ratesMeta.markupPct, ratesMeta.roundingProfile],
  );

  const formatPriceFrom = useCallback(
    (amount: number, fromCurrency: string): string => {
      if (amount == null || Number.isNaN(amount)) return '';
      if (!fxDisplayReady) return PRICE_LOADING_TEXT;
      const fromRate = fromCurrency === DEFAULT_CURRENCY ? 1 : (rates[fromCurrency] ?? 0);
      // Never show unconverted source amount as target currency while rates are missing.
      if (!fromRate) {
        return fromCurrency.toUpperCase() === currency
          ? intlCurrencyAmount(locale, currency, amount)
          : PRICE_LOADING_TEXT;
      }
      const amountInUsd = fromRate ? amount / fromRate : amount;
      if (isWorkspaceRoute || !canUseFxForThisUser || currency === DEFAULT_CURRENCY) {
        // Backoffice and FX-disabled mode always show base USD amounts.
        return intlCurrencyAmount(locale, DEFAULT_CURRENCY, Number(amountInUsd));
      }
      const toRate = currency === DEFAULT_CURRENCY ? 1 : (rates[currency] ?? 0);
      if (!toRate) return currency === DEFAULT_CURRENCY ? intlCurrencyAmount(locale, currency, amountInUsd) : PRICE_LOADING_TEXT;
      const markupFactor =
        isWorkspaceRoute || currency === DEFAULT_CURRENCY
          ? 1
          : 1 + Number(ratesMeta.markupPct ?? 0) / 100;
      const rawValue = amountInUsd * toRate * markupFactor;
      const value = isWorkspaceRoute
        ? Number(rawValue.toFixed(2))
        : applyFxRounding(rawValue, ratesMeta.roundingProfile);
      return intlCurrencyAmount(locale, currency, value);
    },
    [canUseFxForThisUser, currency, fxDisplayReady, isWorkspaceRoute, rates, locale, ratesMeta.markupPct, ratesMeta.roundingProfile],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      setCurrency,
      isCurrencySwitching,
      supportedCurrencies:
        canUseFxForThisUser
          ? supportedCurrencies
          : supportedCurrencies.filter((c) => c.code === DEFAULT_CURRENCY),
      rates,
      ratesMeta,
      formatPrice,
      formatPriceFrom,
      formatMajorInCurrency,
      refreshRates,
      refreshSupported,
      loading,
    }),
    [
      currency,
      setCurrency,
      isCurrencySwitching,
      supportedCurrencies,
      canUseFxForThisUser,
      rates,
      ratesMeta,
      formatPrice,
      formatPriceFrom,
      formatMajorInCurrency,
      refreshRates,
      refreshSupported,
      loading,
    ],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  return React.useContext(CurrencyContext);
}
